import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ArrowDownToLine, ArrowUpFromLine, CalendarClock, MapPin, Truck, Clock, Box, Package } from "lucide-react";
import { apiUrl } from "@/lib/api-config";

type Entry = {
  id: number;
  date: string;
  time_slot: string | null;
  type: "inbound" | "outbound" | "event";
  customer: string | null;
  destination: string | null;
  material: string | null;
  quantity: string | null;
  driver: string | null;
  notes: string | null;
};

type Payload = {
  org_name: string | null;
  date: string;
  entries: Entry[];
};

function formatHumanDate(iso: string) {
  return new Date(iso + "T12:00:00").toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function typeStyle(type: Entry["type"]) {
  if (type === "inbound") return { label: "Inbound", color: "blue", Icon: ArrowDownToLine };
  if (type === "event") return { label: "Event", color: "purple", Icon: CalendarClock };
  return { label: "Outbound", color: "green", Icon: ArrowUpFromLine };
}

export default function SharedCalendarDay() {
  const { orgId, date } = useParams<{ orgId: string; date: string }>();
  const [data, setData] = useState<Payload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId || !date) return;
    setLoading(true);
    setError(null);
    fetch(apiUrl(`/api/public/calendar/${orgId}/${date}`))
      .then(async (res) => {
        if (!res.ok) throw new Error(`Could not load schedule (${res.status})`);
        return res.json();
      })
      .then((d) => setData(d))
      .catch((e) => setError(e.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, [orgId, date]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-white/95 dark:bg-gray-900/95 backdrop-blur border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-md mx-auto px-4 py-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#264027] dark:text-emerald-400">
            Schedule
          </p>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white mt-0.5 leading-tight">
            {date ? formatHumanDate(date) : "—"}
          </h1>
          {data?.org_name && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{data.org_name}</p>
          )}
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-5 pb-24">
        {loading && (
          <div className="text-center py-16 text-sm text-gray-400">Loading schedule…</div>
        )}

        {!loading && error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-sm text-red-700 dark:text-red-400">
            {error}
          </div>
        )}

        {!loading && !error && data && data.entries.length === 0 && (
          <div className="text-center py-16">
            <CalendarClock size={36} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Nothing scheduled for this day.</p>
          </div>
        )}

        {!loading && !error && data && data.entries.length > 0 && (
          <div className="space-y-3">
            {data.entries.map((entry) => {
              const { label, color, Icon } = typeStyle(entry.type);
              return (
                <article
                  key={entry.id}
                  className={`
                    relative rounded-2xl border p-4 bg-white dark:bg-gray-900 shadow-sm
                    ${color === "blue" ? "border-blue-200 dark:border-blue-900/50" : ""}
                    ${color === "green" ? "border-green-200 dark:border-green-900/50" : ""}
                    ${color === "purple" ? "border-purple-200 dark:border-purple-900/50" : ""}
                  `}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span
                      className={`
                        inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider
                        ${color === "blue" ? "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" : ""}
                        ${color === "green" ? "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300" : ""}
                        ${color === "purple" ? "bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300" : ""}
                      `}
                    >
                      <Icon size={12} />
                      {label}
                    </span>
                    {entry.time_slot && (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-700 dark:text-gray-300">
                        <Clock size={12} />
                        {entry.time_slot}
                      </span>
                    )}
                  </div>

                  {entry.customer && (
                    <h2 className="text-base font-bold text-gray-900 dark:text-white leading-snug mb-2 break-words">
                      {entry.customer}
                    </h2>
                  )}

                  <div className="space-y-1.5">
                    {entry.destination && (
                      <p className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <MapPin size={14} className="text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0" />
                        <span className="break-words">{entry.destination}</span>
                      </p>
                    )}
                    {entry.driver && (
                      <p className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <Truck size={14} className="text-gray-400 dark:text-gray-500 flex-shrink-0" />
                        <span>{entry.driver}</span>
                      </p>
                    )}
                    {entry.material && (
                      <p className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <Box size={14} className="text-gray-400 dark:text-gray-500 flex-shrink-0" />
                        <span>{entry.material}</span>
                      </p>
                    )}
                    {entry.quantity && (
                      <p className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <Package size={14} className="text-gray-400 dark:text-gray-500 flex-shrink-0" />
                        <span>{entry.quantity}</span>
                      </p>
                    )}
                  </div>

                  {entry.notes && (
                    <p className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 text-sm text-gray-600 dark:text-gray-400 italic break-words">
                      {entry.notes}
                    </p>
                  )}
                </article>
              );
            })}
          </div>
        )}

        <p className="mt-8 text-center text-[11px] text-gray-400 dark:text-gray-600">
          Powered by CompostDeveloper
        </p>
      </div>
    </div>
  );
}
